/**
 * Cache for userId → profile with optional sealed-keystore persistence.
 *
 * Without persistence: every tab reload starts with an empty cache, so the
 * first render shows the shortId placeholder for every member until the
 * batched `/users/by-ids` round-trip lands (50-200ms). With a sealed-store
 * backing we hydrate known profiles on boot, so returning users see the
 * last-known display name immediately and we only hit the network when a
 * profile looks stale.
 *
 * Storage key layout: `chat:member:v1:<userId>` → { profile | null, at }.
 * Stored inside the passphrase-sealed keystore so the set of usernames the
 * user has interacted with doesn't leak from IndexedDB at rest.
 *
 * TTLs:
 *   - HIT_TTL_MS  24h    Display names change rarely; users still see
 *                        correct info after a day without any network op,
 *                        and a background refresh after expiry updates.
 *   - MISS_TTL_MS 10m    The server may not yet know a user that just
 *                        completed claim — don't lock out for long.
 *   - STALE_REFRESH_MS 1h After this point we return the cached value AND
 *                        schedule a background refresh. No user-visible
 *                        delay; keeps the cache fresh on active devices.
 */
import type { KeyStore } from '@chat/crypto';
import type { UserId } from '@chat/protocol';
import type { ChatClient } from './api.js';

export interface MemberProfile {
  userId: string;
  username?: string;
  displayName?: string;
  /** R2 object id of the user's avatar (under media/<userId>/<id>). */
  avatarMediaId?: string;
}

interface CacheEntry {
  profile: MemberProfile | null;
  /** ms-since-epoch — value is "good" up to this point. */
  expiresAt: number;
  /** ms-since-epoch when we last fetched from the server. */
  fetchedAt: number;
}

interface StoredEntry {
  profile: MemberProfile | null;
  fetchedAt: number;
}

const HIT_TTL_MS = 24 * 60 * 60_000;
const MISS_TTL_MS = 10 * 60_000;
const STALE_REFRESH_MS = 60 * 60_000;
const MEMBER_PREFIX = 'chat:member:v1:';
const VERSION_KEY = 'chat:member-version:v1';
/**
 * How often to ping the server for the directory version. An hour
 * matches the STALE_REFRESH_MS window — beyond it the per-entry
 * refresh would fire anyway; the version check supersedes it with
 * a single cheap round-trip that usually returns "nothing changed".
 */
const VERSION_POLL_INTERVAL_MS = 60 * 60_000;

function storageKey(userId: string): string {
  return `${MEMBER_PREFIX}${userId}`;
}

export interface MemberDirectoryOptions {
  readonly store?: KeyStore;
}

export class MemberDirectory {
  readonly #client: ChatClient;
  readonly #tokenProvider: () => string;
  readonly #cache = new Map<string, CacheEntry>();
  readonly #listeners = new Set<() => void>();
  /** Blob-URL cache keyed by `${userId}:${avatarMediaId}`. */
  readonly #avatarUrls = new Map<string, string>();
  /** In-flight avatar downloads — dedupe concurrent requests. */
  readonly #avatarFetches = new Set<string>();
  readonly #store: KeyStore | undefined;
  #pendingIds = new Set<string>();
  #pendingFlush: Promise<void> | undefined;
  #hydrationPromise: Promise<void> | undefined;
  /** Last directory-version seen from the server. `undefined` = not yet checked. */
  #knownVersion: number | undefined;
  /** ms-since-epoch of the last server version-check, to throttle polling. */
  #lastVersionCheckAt = 0;
  /** In-flight version-check promise so concurrent callers coalesce. */
  #versionCheckInFlight: Promise<boolean> | undefined;

  constructor(
    client: ChatClient,
    tokenProvider: () => string,
    options: MemberDirectoryOptions = {},
  ) {
    this.#client = client;
    this.#tokenProvider = tokenProvider;
    this.#store = options.store;
    if (this.#store) {
      this.#hydrationPromise = this.#hydrate();
    }
  }

  /**
   * Synchronous accessor — returns the best currently-known label for
   * this userId. If we don't have it yet, kicks off a background fetch
   * and returns a placeholder. Re-renders are driven by subscribe().
   */
  label(userId: string): string {
    const entry = this.#lookupFresh(userId);
    if (entry?.profile) {
      const p = entry.profile;
      return p.displayName ?? p.username ?? shortId(userId);
    }
    this.#maybeRefresh(userId);
    return shortId(userId);
  }

  /**
   * Returns the cached profile if any — undefined for "unknown", with
   * async hydration started. Null-profile cache entries signal "server
   * doesn't know this userId".
   */
  profile(userId: string): MemberProfile | undefined {
    const entry = this.#lookupFresh(userId);
    if (entry) return entry.profile ?? undefined;
    this.#maybeRefresh(userId);
    return undefined;
  }

  /** Prime the cache with userIds that should be resolved now. */
  prefetch(userIds: readonly string[]): void {
    for (const id of userIds) this.#maybeRefresh(id);
  }

  /**
   * Get a blob-URL for this user's avatar, downloading it on first
   * request and caching per (userId, avatarMediaId). Returns
   * undefined while the profile or the avatar download is in flight
   * (or if the user has no avatar). A re-render is triggered via
   * subscribe() once the URL is ready.
   */
  avatarUrl(userId: string): string | undefined {
    const profile = this.profile(userId);
    if (!profile?.avatarMediaId) return undefined;
    return this.avatarUrlFor(userId, profile.avatarMediaId);
  }

  /**
   * Resolve a blob-URL for an explicit (uploaderUserId, mediaId)
   * pair. Used by avatarUrl above for personal avatars and by the
   * conversation header for shared group avatars (which live under
   * the uploader's R2 prefix but with a different mediaId than the
   * uploader's own profile picture).
   */
  avatarUrlFor(uploaderUserId: string, mediaId: string): string | undefined {
    const cacheKey = `${uploaderUserId}:${mediaId}`;
    const cached = this.#avatarUrls.get(cacheKey);
    if (cached) return cached;
    if (this.#avatarFetches.has(cacheKey)) return undefined;
    this.#avatarFetches.add(cacheKey);
    void (async () => {
      try {
        const { bytes, mime } = await this.#client.downloadAvatar(
          this.#tokenProvider(),
          uploaderUserId as UserId,
          mediaId,
        );
        const copy = new Uint8Array(bytes);
        const blob = new Blob([copy.buffer], { type: mime });
        const url = URL.createObjectURL(blob);
        this.#avatarUrls.set(cacheKey, url);
        this.#emit();
      } catch {
        // Leave the fetch flag set for a bit so we don't hammer a
        // missing avatar repeatedly.
      }
    })();
    return undefined;
  }

  /** React-friendly subscription — called whenever cache state changes. */
  subscribe(fn: () => void): () => void {
    this.#listeners.add(fn);
    return () => {
      this.#listeners.delete(fn);
    };
  }

  /**
   * Waits for the keystore hydration to complete. Callers that want the
   * first render to include persisted names can `await ready()` before
   * mounting the view; callers that are fine with a quick flicker can
   * skip it (unhydrated lookups just return shortId until hydration
   * settles, then fire a listener notification).
   */
  async ready(): Promise<void> {
    if (this.#hydrationPromise) await this.#hydrationPromise;
  }

  /** Merge a profile we already have (e.g. from the logged-in user). */
  seed(profile: MemberProfile): void {
    const now = Date.now();
    this.#cache.set(profile.userId, {
      profile,
      expiresAt: now + HIT_TTL_MS,
      fetchedAt: now,
    });
    void this.#persistEntry(profile.userId);
    this.#emit();
  }

  #lookupFresh(userId: string): CacheEntry | undefined {
    const entry = this.#cache.get(userId);
    if (!entry) return undefined;
    if (entry.expiresAt <= Date.now()) return undefined;
    return entry;
  }

  #maybeRefresh(userId: string): void {
    const entry = this.#cache.get(userId);
    const now = Date.now();
    if (!entry || entry.expiresAt <= now) {
      // Unknown / expired → we have no cached value to serve, so the
      // user would see a placeholder. Fetch directly; the version
      // check isn't useful here because we'd need to fetch either way.
      this.#scheduleFetch(userId);
      return;
    }
    // Fresh but aging past the stale-refresh threshold. Instead of
    // fetching the profile immediately, consult the directory-version
    // endpoint: if nothing in the directory has changed since our last
    // look, skip the per-profile refresh entirely. A mismatch kicks
    // off the normal refresh path.
    if (now - entry.fetchedAt >= STALE_REFRESH_MS) {
      void this.#ensureDirectoryVersion().then((changed) => {
        if (changed) this.#scheduleFetch(userId);
      });
    }
  }

  /**
   * Check the server's directory-version and return true if the
   * directory has changed since we last looked. Coalesces concurrent
   * callers into a single round-trip and throttles to at most one
   * real request per VERSION_POLL_INTERVAL_MS.
   */
  async #ensureDirectoryVersion(): Promise<boolean> {
    const now = Date.now();
    if (
      now - this.#lastVersionCheckAt < VERSION_POLL_INTERVAL_MS &&
      this.#knownVersion !== undefined
    ) {
      return false;
    }
    if (this.#versionCheckInFlight) return this.#versionCheckInFlight;
    const pending = (async (): Promise<boolean> => {
      try {
        const remote = await this.#client.getDirectoryVersion(this.#tokenProvider());
        this.#lastVersionCheckAt = Date.now();
        const previous = this.#knownVersion;
        this.#knownVersion = remote;
        if (previous === undefined) {
          // First check in this session. If we loaded a version from
          // storage earlier (via persistVersion) it'll have already
          // set #knownVersion before this point; see #hydrate.
          await this.#persistVersion(remote);
          return true;
        }
        if (remote !== previous) {
          await this.#persistVersion(remote);
          return true;
        }
        return false;
      } catch {
        return false;
      } finally {
        this.#versionCheckInFlight = undefined;
      }
    })();
    this.#versionCheckInFlight = pending;
    return pending;
  }

  #emit(): void {
    for (const fn of this.#listeners) fn();
  }

  #scheduleFetch(userId: string): void {
    if (this.#pendingIds.has(userId)) return;
    this.#pendingIds.add(userId);
    if (this.#pendingFlush) return;
    // Coalesce sync-iteration requests into a single network hop.
    this.#pendingFlush = Promise.resolve().then(async () => {
      const ids = [...this.#pendingIds];
      this.#pendingIds = new Set();
      this.#pendingFlush = undefined;
      try {
        const profiles = await this.#client.lookupUsers(this.#tokenProvider(), ids);
        const now = Date.now();
        for (const id of ids) {
          const profile = profiles[id] ?? null;
          this.#cache.set(id, {
            profile,
            expiresAt: now + (profile ? HIT_TTL_MS : MISS_TTL_MS),
            fetchedAt: now,
          });
          void this.#persistEntry(id);
        }
        this.#emit();
      } catch {
        // Transient failure. Leave the cache alone so a stale-but-
        // persisted entry keeps answering; the next tick will retry.
      }
    });
  }

  async #hydrate(): Promise<void> {
    if (!this.#store) return;
    try {
      const keys = await this.#store.list(MEMBER_PREFIX);
      for (const key of keys) {
        const raw = await this.#store.get(key);
        if (!raw) continue;
        try {
          const parsed = JSON.parse(new TextDecoder().decode(raw)) as StoredEntry & {
            userId?: string;
          };
          const userId =
            parsed.userId ??
            (key.startsWith(MEMBER_PREFIX) ? key.slice(MEMBER_PREFIX.length) : undefined);
          if (!userId) continue;
          const fetchedAt = typeof parsed.fetchedAt === 'number' ? parsed.fetchedAt : 0;
          const profile =
            parsed.profile && typeof parsed.profile === 'object' ? parsed.profile : null;
          const ttl = profile ? HIT_TTL_MS : MISS_TTL_MS;
          this.#cache.set(userId, {
            profile,
            expiresAt: fetchedAt + ttl,
            fetchedAt,
          });
        } catch {
          // corrupt record — skip
        }
      }
      // Load the last-seen directory version so the first #ensureDirectoryVersion
      // check can short-circuit when the server's value hasn't moved.
      const versionRaw = await this.#store.get(VERSION_KEY);
      if (versionRaw) {
        try {
          const n = Number.parseInt(new TextDecoder().decode(versionRaw), 10);
          if (Number.isFinite(n) && n >= 0) this.#knownVersion = n;
        } catch {
          // ignore corrupt
        }
      }
    } catch {
      // Keystore errors (sealed-store not ready etc.) leave us empty;
      // the network fallback still works.
    }
    this.#emit();
  }

  async #persistVersion(version: number): Promise<void> {
    if (!this.#store) return;
    try {
      await this.#store.put(VERSION_KEY, new TextEncoder().encode(String(version)));
    } catch {
      // best-effort
    }
  }

  async #persistEntry(userId: string): Promise<void> {
    if (!this.#store) return;
    const entry = this.#cache.get(userId);
    if (!entry) return;
    try {
      const payload: StoredEntry & { userId: string } = {
        userId,
        profile: entry.profile,
        fetchedAt: entry.fetchedAt,
      };
      await this.#store.put(storageKey(userId), new TextEncoder().encode(JSON.stringify(payload)));
    } catch {
      // Best-effort: persistence is a latency win, not a correctness
      // requirement. Losing a write means the next mount fetches again.
    }
  }
}

export function shortId(userId: string): string {
  // Drop the `user-` prefix and keep the first 6 base64url chars so
  // humans get a short, unique-ish tag. Doesn't leak full pubkey
  // material in UI surfaces.
  const stripped = userId.startsWith('user-') ? userId.slice(5) : userId;
  return stripped.slice(0, 6);
}
